import { useLocation } from '@tanstack/react-router';
import { getWorkspaceIdFromUrl } from '@/utils/workspace-id';

/**
 * Builds the target href for a flag-gated migration handoff: the migrated
 * route forwarding to its `journeys` twin (or the reverse), with the query
 * string and hash carried across — Ember transitions preserve query params,
 * so both directions do too.
 *
 * `buildTargetHref` receives the workspace id and returns the target path
 * (typically `router.buildLocation({ to, params }).href`, which keeps the
 * route path typed at the call site).
 *
 * The workspace id comes off the reactive location rather than route params:
 * `/workspaces/last` is rewritten by a bridge-synced navigation that leaves
 * route params stale. `null` until the id lands — callers hold a loader.
 */
export function useMigrationHandoffHref(buildTargetHref: (workspaceId: string) => string): string | null {
  const { pathname, searchStr, hash } = useLocation();
  const workspaceId = getWorkspaceIdFromUrl(pathname);

  if (workspaceId == null) {
    return null;
  }

  const href = buildTargetHref(String(workspaceId));
  return `${href}${searchStr}${hash === '' ? '' : `#${hash}`}`;
}
